/**
 * 관리자 > 물품 대여 (/admin/rental)
 * 탭: 예약·입금 · 캘린더 · 현장 수령·반납 · 물품·체크리스트 · 주의 교회 · 설정
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, MenuItem, Paper, Snackbar, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { addDays, overdueDays } from '../logic';
import RentalCalendar from '../RentalCalendar';
import RequestForm from '../RequestForm';
import HandoverRecords from '../HandoverRecords';
import HandoverForm from './HandoverForm';
import { fmtLong, fmtShort, lineText, payDeadline, RentalFlag, RentalItem, RentalSettings, rentalApi, Reservation } from '../api';

const TABS = [
  { key: 'list', label: '예약·입금' },
  { key: 'cal', label: '캘린더' },
  { key: 'field', label: '현장 수령·반납' },
  { key: 'items', label: '물품·체크리스트' },
  { key: 'flags', label: '주의 교회' },
  { key: 'settings', label: '설정' },
];
const HEAD_BG = 'linear-gradient(100deg,#ecfdf5,#a7f3d0 60%,#6ee7b7)';
const HEAD_FG = '#064e3b';
const CHIP: Record<string, { bg: string; fg: string }> = {
  입금대기: { bg: '#fef3c7', fg: '#92400e' }, 대여확정: { bg: '#dbeafe', fg: '#1e40af' }, 대여중: { bg: '#dcfce7', fg: '#166534' },
  반납완료: { bg: '#f1f5f9', fg: '#475569' }, 확인필요: { bg: '#fee2e2', fg: '#b91c1c' }, 반납지연: { bg: '#fee2e2', fg: '#b91c1c' },
  반려: { bg: '#f1f5f9', fg: '#64748b' }, 취소: { bg: '#f1f5f9', fg: '#64748b' },
};
const SChip: React.FC<{ s: string; extra?: string }> = ({ s, extra }) => <Chip size="small" label={`${s}${extra || ''}`} sx={{ bgcolor: CHIP[s]?.bg, color: CHIP[s]?.fg, fontWeight: 600 }} />;
const FILTERS = ['입금대기', '대여확정', '대여중', '반납지연', '확인필요', '반납완료', '반려·취소', '전체'];

type Ctx = { role?: string | null };

const RentalAdminPage: React.FC = () => {
  const ctx = (useOutletContext<Ctx>() || {}) as Ctx;
  const readOnly = ctx.role === 'mini';
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab')! : 'list';
  const go = (key: string, extra: Record<string, string> = {}) => setParams({ tab: key, ...extra });
  const [today, setToday] = useState('');
  const [list, setList] = useState<Reservation[]>([]);
  const [flags, setFlags] = useState<RentalFlag[]>([]);
  const [items, setItems] = useState<RentalItem[]>([]);
  const [settings, setSettings] = useState<RentalSettings | null>(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const reload = useCallback(async () => {
    try {
      const [a, b, c] = await Promise.all([rentalApi.adminList(), rentalApi.items(), rentalApi.settings()]);
      setToday(a.today); setList(a.reservations); setFlags(a.flags); setItems(b); setSettings(c); setError('');
    } catch (e: any) { setError(e.message); }
  }, []);
  // 관리자 암호(BT 인증) 확인이 끝난 뒤에 불러온다 — 그 전에는 API 가 401
  useEffect(() => {
    if (!ctx.role) return undefined;
    reload();
    const t = setInterval(reload, 60000);
    return () => clearInterval(t);
  }, [reload, ctx.role]);

  return (
    <Box sx={{ mx: { xs: -2, sm: -3 }, mt: -3, bgcolor: '#f8fffb', minHeight: 'calc(100vh - 64px)' }}>
      <Box sx={{ background: HEAD_BG, color: HEAD_FG, px: { xs: 2, sm: 3 }, pt: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={800}>물품 대여</Typography>
            <Typography fontSize={13} sx={{ opacity: 0.85 }}>예약 → 입금 확인·승인 → 수령 서명 → 반납 서명</Typography>
          </Box>
          <Button size="small" color="inherit" variant="outlined" onClick={() => window.open('/rental', '_blank')}>교회 신청 화면 보기</Button>
          <Button size="small" color="inherit" variant="outlined" onClick={() => go('field')}>📱 현장 수령·반납</Button>
        </Box>
        <Tabs value={tab} onChange={(_, v) => go(v)} variant="scrollable" scrollButtons="auto"
          sx={{ mt: 1.5, '& .MuiTab-root': { color: HEAD_FG, opacity: 0.75, fontWeight: 700 }, '& .Mui-selected': { opacity: 1, color: `${HEAD_FG} !important` }, '& .MuiTabs-indicator': { bgcolor: HEAD_FG, height: 3 } }}>
          {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
        </Tabs>
      </Box>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {readOnly && <Alert severity="info" sx={{ mb: 2 }}>조회 전용 계정이에요. 승인·수정은 관리자 계정으로 하세요.</Alert>}
        {!ctx.role ? <Typography color="text.secondary" sx={{ py: 8, textAlign: 'center' }}>관리자 암호를 입력하면 열려요.</Typography>
          : !settings ? <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}>{!error && <CircularProgress />}</Box> : (
          <>
            {tab === 'list' && <ListTab list={list} flags={flags} items={items} settings={settings} today={today} reload={reload} toast={setToast} goField={(id, t) => go('field', { id, type: t })} />}
            {tab === 'cal' && <CalTab list={list} items={items} settings={settings} today={today} />}
            {tab === 'field' && <FieldTab list={list} items={items} settings={settings} today={today} reload={reload} toast={setToast}
              target={params.get('id') ? { id: params.get('id')!, type: (params.get('type') as 'pickup' | 'ret') || 'pickup' } : null}
              setTarget={(t) => (t ? go('field', { id: t.id, type: t.type }) : go('field'))} />}
            {tab === 'items' && <ItemsTab items={items} reload={reload} toast={setToast} />}
            {tab === 'flags' && <FlagsTab flags={flags} reload={reload} toast={setToast} />}
            {tab === 'settings' && <SettingsTab settings={settings} reload={reload} toast={setToast} />}
          </>
        )}
      </Box>
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast('')} message={toast}
        action={<IconButton size="small" color="inherit" onClick={() => setToast('')}><Close fontSize="small" /></IconButton>} />
    </Box>
  );
};

// ───────── 예약·입금 ─────────
const ListTab: React.FC<{
  list: Reservation[]; flags: RentalFlag[]; items: RentalItem[]; settings: RentalSettings; today: string; reload: () => Promise<void>;
  toast: (m: string) => void; goField: (id: string, t: 'pickup' | 'ret') => void;
}> = ({ list, flags, items, settings, today, reload, toast, goField }) => {
  const [filter, setFilter] = useState('입금대기');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [rejectFor, setRejectFor] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);
  const [cert, setCert] = useState<Reservation | null>(null);
  const [err, setErr] = useState('');
  const weekEnd = addDays(today, 7 - new Date(`${today}T12:00:00+09:00`).getDay());
  const cnt = {
    w: list.filter((r) => r.status === '입금대기').length,
    p: list.filter((r) => r.status === '대여확정' && r.start >= today && r.start <= weekEnd).length,
    u: list.filter((r) => r.status === '대여중').length,
    l: list.filter((r) => overdueDays(r, today) > 0).length,
    c: list.filter((r) => r.status === '확인필요').length,
  };
  const shown = list.filter((r) => {
    if (filter === '전체') return true;
    if (filter === '반려·취소') return r.status === '반려' || r.status === '취소';
    if (filter === '반납지연') return overdueDays(r, today) > 0;
    return r.status === filter;
  }).filter((r) => !q || `${r.churchName} ${r.regNo} ${r.contact} ${r.no}`.toLowerCase().includes(q.toLowerCase()));
  const flagOf = (regNo?: string) => flags.filter((f) => f.regNo === regNo);
  const act = async (fn: () => Promise<any>, msg: string) => {
    setErr('');
    try { await fn(); await reload(); toast(msg); } catch (e: any) { setErr(e.message); }
  };
  const loadHandovers = useMemo(() => (id: string) => rentalApi.adminHandovers(id), []);
  const Kpi = ({ k, v, red, f }: { k: string; v: number; red?: boolean; f: string }) => (
    <Paper variant="outlined" onClick={() => setFilter(f)} sx={{ p: 1.75, borderRadius: 2, cursor: 'pointer', borderColor: filter === f ? HEAD_FG : undefined }}>
      <Typography fontSize={12.5} color="text.secondary">{k}</Typography>
      <Typography fontSize={26} fontWeight={700} color={red && v ? 'error.main' : 'text.primary'}>{v}</Typography>
    </Paper>
  );
  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', sm: 'repeat(5,minmax(0,1fr))' }, gap: 1.5, mb: 2 }}>
        <Kpi k="입금 대기" v={cnt.w} f="입금대기" />
        <Kpi k="이번 주 수령" v={cnt.p} f="대여확정" />
        <Kpi k="대여 중" v={cnt.u} f="대여중" />
        <Kpi k="반납 지연" v={cnt.l} red f="반납지연" />
        <Kpi k="확인 필요" v={cnt.c} red f="확인필요" />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} sx={{ flexWrap: 'wrap', bgcolor: '#fff' }}>
          {FILTERS.map((f) => <ToggleButton key={f} value={f} sx={{ fontWeight: 700 }}>{f}</ToggleButton>)}
        </ToggleButtonGroup>
        <TextField size="small" placeholder="교회 · 등록번호 · 담당자 · 예약번호" value={q} onChange={(e) => setQ(e.target.value)} sx={{ flex: 1, minWidth: 200, bgcolor: '#fff' }} />
        <Button variant="outlined" onClick={() => setAdding(!adding)}>{adding ? '예약 추가 닫기' : '+ 전화 접수 예약 추가'}</Button>
      </Box>
      <Collapse in={adding} unmountOnExit>
        <Box sx={{ mb: 2 }}>
          <RequestForm mode="admin" items={items.filter((i) => i.active !== false)} settings={settings} reservations={list} today={today}
            onSubmit={async (body) => { await rentalApi.adminCreate(body as any); setAdding(false); setFilter('입금대기'); await reload(); toast('예약을 추가했어요. 입금 대기로 등록됐어요.'); }} />
        </Box>
      </Collapse>
      {err && <Alert severity="error" sx={{ mb: 1.5, whiteSpace: 'pre-line' }} onClose={() => setErr('')}>{err}</Alert>}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>{['예약번호', '등록번호', '교회명 / 담당자', '물품', '기간', '방법', '금액', '입금', '수령', '반납', '상태', ''].map((h) => <TableCell key={h} sx={{ whiteSpace: 'nowrap' }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {!shown.length && <TableRow><TableCell colSpan={12} align="center" sx={{ py: 4, color: 'text.secondary' }}>「{filter}」 상태인 예약이 없어요.</TableCell></TableRow>}
            {shown.map((r) => {
              const late = overdueDays(r, today);
              const ds = late ? '반납지연' : r.status;
              const fl = flagOf(r.regNo);
              const payLate = r.status === '입금대기' && Date.now() > payDeadline(r, settings).getTime();
              return (
                <React.Fragment key={r._id}>
                  <TableRow hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.no}</TableCell>
                    <TableCell>{r.regNo}</TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <b>{r.churchName}</b>{fl.length > 0 && <Typography component="span" color="error.main" fontWeight={800} title="주의 교회"> ⚠</Typography>}
                      <Typography fontSize={11.5} color="text.secondary">{r.contact} · {r.phone}{fl[0] ? ` · ${fl[0].type}${fl[0].lateDays ? ` ${fl[0].lateDays}일` : ''}` : ''}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{lineText(r.lines)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtShort(r.start)} ~ {fmtShort(r.end)}</TableCell>
                    <TableCell>{r.method}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{(r.total || 0).toLocaleString('ko-KR')}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.paid ? <Chip size="small" label={`${r.paidAt ? new Date(r.paidAt).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' }) : ''} 입금`} sx={{ bgcolor: '#dcfce7', color: '#166534' }} />
                        : r.status === '입금대기' ? (
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Button size="small" variant="contained" onClick={() => act(() => rentalApi.approve(r._id), `${r.churchName} 입금 확인 · 승인했어요.`)}>입금 확인 · 승인</Button>
                            <Button size="small" color="error" variant="outlined" onClick={() => { setRejectFor(r._id); setReason(''); }}>반려</Button>
                            {payLate && <Chip size="small" label="기한 초과" sx={{ bgcolor: '#fee2e2', color: '#b91c1c' }} />}
                          </Box>
                        ) : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.pickup ? <Chip size="small" label={`${r.pickup.at.slice(5, 10)} ${r.pickup.renter} ✍`} onClick={() => setCert(r)} />
                        : r.status === '대여확정' ? <Button size="small" variant="outlined" onClick={() => goField(r._id, 'pickup')}>수령 처리</Button> : '—'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.ret ? <Box><Chip size="small" label={`${r.ret.at.slice(5, 10)} ${r.ret.renter} ✍`} onClick={() => setCert(r)} />{r.ret.issue && <Typography fontSize={11} color="error.main" maxWidth={180} noWrap title={r.ret.issue}>{r.ret.issue}</Typography>}</Box>
                        : r.status === '대여중' ? <Button size="small" variant="outlined" onClick={() => goField(r._id, 'ret')}>반납 처리</Button> : '—'}
                    </TableCell>
                    <TableCell><SChip s={ds} extra={late ? ` ${late}일` : ''} /></TableCell>
                    <TableCell><Button size="small" onClick={() => setOpen(open === r._id ? null : r._id)}>{open === r._id ? '닫기' : '상세'}</Button></TableCell>
                  </TableRow>
                  {rejectFor === r._id && (
                    <TableRow><TableCell colSpan={12} sx={{ bgcolor: '#fffbeb' }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField size="small" label="반려 사유" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ minWidth: 280, bgcolor: '#fff' }} placeholder="예: 해당 기간 행사 일정과 겹침" />
                        <Button color="error" variant="contained" onClick={() => { setRejectFor(null); act(() => rentalApi.reject(r._id, reason), '반려했어요.'); }}>반려하기</Button>
                        <Button onClick={() => setRejectFor(null)}>그만두기</Button>
                      </Box>
                    </TableCell></TableRow>
                  )}
                  {open === r._id && <TableRow><TableCell colSpan={12} sx={{ bgcolor: '#f8fffb' }}><Detail r={r} reload={reload} toast={toast} /></TableCell></TableRow>}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography fontSize={11.5} color="text.secondary" mt={1}>✍ 누르면 서명·사진·체크리스트 확인서 · ⚠ 주의 교회</Typography>
      <HandoverRecords reservation={cert} load={loadHandovers} onClose={() => setCert(null)} />
    </Box>
  );
};

const Detail: React.FC<{ r: Reservation; reload: () => Promise<void>; toast: (m: string) => void }> = ({ r, reload, toast }) => {
  const [start, setStart] = useState(r.start);
  const [end, setEnd] = useState(r.end);
  const [warn, setWarn] = useState('');
  const [payer, setPayer] = useState(r.payer || r.churchName);
  const [total, setTotal] = useState(String(r.total || 0));
  const [refund, setRefund] = useState(r.refund || '');
  const [trackOut, setTrackOut] = useState(r.trackOut || '');
  const [trackIn, setTrackIn] = useState(r.trackIn || '');
  const [memo, setMemo] = useState(r.adminMemo || '');
  const [err, setErr] = useState('');
  const save = async (body: Record<string, any>, msg: string) => {
    setErr('');
    try { await rentalApi.update(r._id, body); setWarn(''); await reload(); toast(msg); } catch (e: any) {
      if (e.status === 409) setWarn(e.message); else setErr(e.message);
    }
  };
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0,1fr))' }, gap: 2, py: 1 }}>
      <Box sx={{ display: 'grid', gap: 1, alignContent: 'start' }}>
        <Typography fontSize={13} fontWeight={800} color={HEAD_FG}>기간 수정 (관리자)</Typography>
        <TextField size="small" type="date" label="수령일" value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="반납일" value={end} onChange={(e) => setEnd(e.target.value)} InputLabelProps={{ shrink: true }} />
        {warn && <Alert severity="warning" action={<Button color="inherit" size="small" onClick={() => save({ start, end, force: true }, '기간을 저장했어요.')}>그래도 저장</Button>}>{warn}</Alert>}
        <Button variant="contained" size="small" onClick={() => save({ start, end }, '기간을 저장했어요. 교회에 알려 주세요.')}>기간 저장</Button>
        {!!r.dateLog?.length && <Typography fontSize={11.5} color="text.secondary" whiteSpace="pre-line">{r.dateLog.join('\n')}</Typography>}
      </Box>
      <Box sx={{ display: 'grid', gap: 1, alignContent: 'start' }}>
        <Typography fontSize={13} fontWeight={800} color={HEAD_FG}>입금·환불</Typography>
        <TextField size="small" label="입금자명" value={payer} onChange={(e) => setPayer(e.target.value)} />
        <TextField size="small" label="금액 조정" value={total} onChange={(e) => setTotal(e.target.value.replace(/\D/g, ''))} />
        <TextField select size="small" label="환불 상태" value={refund} onChange={(e) => setRefund(e.target.value)}>
          {['', '환불 대상', '환불 완료', '환불 없음'].map((x) => <MenuItem key={x || 'none'} value={x}>{x || '—'}</MenuItem>)}
        </TextField>
        {r.method === '택배' && <>
          <TextField size="small" label="발송 송장" value={trackOut} onChange={(e) => setTrackOut(e.target.value)} />
          <TextField size="small" label="회수 송장" value={trackIn} onChange={(e) => setTrackIn(e.target.value)} />
        </>}
        <Button variant="contained" size="small" onClick={() => save({ payer, total: Number(total), refund, ...(r.method === '택배' ? { trackOut, trackIn } : {}) }, '저장했어요.')}>저장</Button>
      </Box>
      <Box sx={{ display: 'grid', gap: 1, alignContent: 'start' }}>
        <Typography fontSize={13} fontWeight={800} color={HEAD_FG}>메모·처리</Typography>
        <TextField size="small" multiline minRows={3} label="관리자 메모" value={memo} onChange={(e) => setMemo(e.target.value)} />
        <Button variant="contained" size="small" onClick={() => save({ adminMemo: memo }, '메모를 저장했어요.')}>메모 저장</Button>
        {r.status === '확인필요' && <Button variant="contained" color="success" size="small" onClick={async () => { try { await rentalApi.resolve(r._id); await reload(); toast('반납 완료로 바꿨어요.'); } catch (e: any) { setErr(e.message); } }}>확인 완료 → 반납 완료</Button>}
        <Typography fontSize={11.5} color="text.secondary">
          {r.source} · 신청 {new Date(r.createdAt).toLocaleString('ko-KR')} · 목적 {r.purpose}{r.memo ? ` · 요청: ${r.memo}` : ''}
          {r.method === '택배' ? ` · 배송: ${r.shipName} ${r.shipPhone || ''} ${r.shipAddr}` : r.time ? ` · 방문 ${r.time}` : ''}
          {r.rejectReason ? ` · 반려 사유: ${r.rejectReason}` : ''}
        </Typography>
        {err && <Alert severity="error">{err}</Alert>}
      </Box>
    </Box>
  );
};

// ───────── 캘린더 ─────────
const CalTab: React.FC<{ list: Reservation[]; items: RentalItem[]; settings: RentalSettings; today: string }> = ({ list, items, settings, today }) => {
  const [month, setMonth] = useState(today.slice(0, 7));
  const [sel, setSel] = useState(today);
  const [filter, setFilter] = useState('all');
  const active = items.filter((i) => i.active !== false);
  const dayRes = list.filter((r) => ['입금대기', '대여확정', '대여중'].includes(r.status) && sel >= r.start && sel <= (r.status === '대여중' && today > r.end ? today : r.end));
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) 320px' }, gap: 2, alignItems: 'start' }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 0 }}>
        <RentalCalendar month={month} onMonth={setMonth} selected={sel} onSelect={setSel} items={active} reservations={list} holidays={settings.holidays} today={today} filter={filter} onFilter={setFilter} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography fontWeight={800} mb={1}>{fmtLong(sel)} 예약</Typography>
        {!dayRes.length && <Typography fontSize={13} color="text.secondary">이날 잡힌 예약이 없어요.</Typography>}
        {dayRes.map((r) => (
          <Box key={r._id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 1, borderBottom: '1px solid #e2e8f0' }}>
            <Box><Typography fontSize={14} fontWeight={700}>{r.churchName}</Typography><Typography fontSize={12} color="text.secondary">{lineText(r.lines)} · {fmtShort(r.start)}~{fmtShort(r.end)}</Typography></Box>
            <SChip s={overdueDays(r, today) ? '반납지연' : r.status} />
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

// ───────── 현장 수령·반납 ─────────
const FieldTab: React.FC<{
  list: Reservation[]; items: RentalItem[]; settings: RentalSettings; today: string; reload: () => Promise<void>; toast: (m: string) => void;
  target: { id: string; type: 'pickup' | 'ret' } | null; setTarget: (t: { id: string; type: 'pickup' | 'ret' } | null) => void;
}> = ({ list, items, settings, today, reload, toast, target, setTarget }) => {
  const r = target ? list.find((x) => x._id === target.id) : null;
  if (target && r) {
    return <HandoverForm key={`${r._id}-${target.type}`} reservation={r} type={target.type} items={items} settings={settings} today={today}
      onBack={() => setTarget(null)} onDone={async (msg) => { await reload(); toast(msg); setTarget(null); }} />;
  }
  const pick = list.filter((x) => x.status === '대여확정').sort((a, b) => (a.start < b.start ? -1 : 1));
  const ret = list.filter((x) => x.status === '대여중').sort((a, b) => (a.end < b.end ? -1 : 1));
  const Row = ({ x, t }: { x: Reservation; t: 'pickup' | 'ret' }) => {
    const late = overdueDays(x, today);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, py: 1.25, borderBottom: '1px solid #eef0f3' }}>
        <Box>
          <Typography fontWeight={700}>{x.churchName} <Typography component="span" fontSize={12} color="text.secondary">{x.no}</Typography></Typography>
          <Typography fontSize={12.5} color={late ? 'error.main' : 'text.secondary'}>{lineText(x.lines)} · {t === 'pickup' ? `수령 ${fmtShort(x.start)}` : `반납 ${fmtShort(x.end)}${late ? ` · 지연 ${late}일` : ''}`}</Typography>
        </Box>
        <Button variant="contained" size="small" onClick={() => setTarget({ id: x._id, type: t })}>{t === 'pickup' ? '수령 처리' : '반납 처리'}</Button>
      </Box>
    );
  };
  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', display: 'grid', gap: 2 }}>
      {target && !r && <Alert severity="warning">예약을 찾을 수 없어요. 목록에서 다시 고르세요.</Alert>}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography fontSize={13} fontWeight={800} color={HEAD_FG}>수령 대상 · 입금 확인 끝난 예약</Typography>
        {pick.length ? pick.map((x) => <Row key={x._id} x={x} t="pickup" />) : <Typography fontSize={13} color="text.secondary" py={1}>수령할 예약이 없어요. 「예약·입금」에서 입금 확인·승인을 하면 여기에 나타나요.</Typography>}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography fontSize={13} fontWeight={800} color={HEAD_FG}>반납 대상 · 대여 중</Typography>
        {ret.length ? ret.map((x) => <Row key={x._id} x={x} t="ret" />) : <Typography fontSize={13} color="text.secondary" py={1}>대여 중인 예약이 없어요.</Typography>}
      </Paper>
      <Typography fontSize={12} color="text.secondary">휴대폰에서 awanaevent.com/admin/rental?tab=field 로 들어오면 이 화면이 바로 열려요.</Typography>
    </Box>
  );
};

// ───────── 물품·체크리스트 ─────────
const ItemsTab: React.FC<{ items: RentalItem[]; reload: () => Promise<void>; toast: (m: string) => void }> = ({ items, reload, toast }) => (
  <Box sx={{ display: 'grid', gap: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography fontSize={13} color="text.secondary">보유 수량이 0이면 교회 화면에서 빌릴 수 없어요. 실제 수량·대여료·부속품 기준을 입력하세요.</Typography>
      <Button variant="contained" onClick={async () => { await rentalApi.createItem(); await reload(); toast('물품을 추가했어요. 이름과 수량을 입력한 뒤 「사용」으로 바꾸세요.'); }}>+ 물품 추가</Button>
    </Box>
    {items.map((it) => <ItemCard key={it._id} item={it} reload={reload} toast={toast} />)}
  </Box>
);

const ItemCard: React.FC<{ item: RentalItem; reload: () => Promise<void>; toast: (m: string) => void }> = ({ item, reload, toast }) => {
  const [d, setD] = useState<RentalItem>(() => ({ ...item, checks: [...(item.checks || [])] }));
  const [err, setErr] = useState('');
  useEffect(() => { setD({ ...item, checks: [...(item.checks || [])] }); }, [item]);
  const num = (k: keyof RentalItem) => (e: React.ChangeEvent<HTMLInputElement>) => setD({ ...d, [k]: Number(e.target.value.replace(/\D/g, '')) || 0 });
  const setCheck = (k: number, patch: any) => setD({ ...d, checks: d.checks!.map((c, i) => (i === k ? { ...c, ...patch } : c)) });
  const save = async () => {
    setErr('');
    try { await rentalApi.updateItem(item._id, d); await reload(); toast(`${d.name} 저장했어요.`); } catch (e: any) { setErr(e.message); }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, display: 'grid', gap: 1.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,minmax(0,1fr))', md: 'repeat(4,minmax(0,1fr))' }, gap: 1.5 }}>
        <TextField size="small" label="물품명" value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} />
        <TextField size="small" label="보유 수량" value={d.total} onChange={num('total')} />
        <TextField size="small" label="수리·점검 중" value={d.repair} onChange={num('repair')} />
        <TextField size="small" label="1회 최대 (0 = 제한 없음)" value={d.maxPerOrder} onChange={num('maxPerOrder')} />
        <TextField size="small" label="대여료 (1회)" value={d.fee} onChange={num('fee')} />
        <TextField select size="small" label="택배" value={d.delivery ? '1' : '0'} onChange={(e) => setD({ ...d, delivery: e.target.value === '1' })}>
          <MenuItem value="0">방문 수령만</MenuItem><MenuItem value="1">택배 가능</MenuItem>
        </TextField>
        <TextField size="small" label="택배비" value={d.deliveryFee} onChange={num('deliveryFee')} disabled={!d.delivery} />
        <TextField select size="small" label="사용 여부" value={d.active === false ? '0' : '1'} onChange={(e) => setD({ ...d, active: e.target.value === '1' })}>
          <MenuItem value="1">사용</MenuItem><MenuItem value="0">숨김</MenuItem>
        </TextField>
      </Box>
      <Box>
        <Typography fontSize={13} fontWeight={800} color={HEAD_FG} mb={1}>부속품 체크리스트 (1개당 기준)</Typography>
        {(d.checks || []).map((c, k) => (
          <Box key={k} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 70px 60px', sm: 'minmax(0,2fr) 90px 80px 130px 70px' }, gap: 1, mb: 1, alignItems: 'center' }}>
            <TextField size="small" label="확인 항목" value={c.label} onChange={(e) => setCheck(k, { label: e.target.value })} />
            <TextField size="small" label="기준" value={c.std} disabled={c.type === 'ok'} onChange={(e) => setCheck(k, { std: Number(e.target.value.replace(/\D/g, '')) || 0 })} />
            <TextField size="small" label="단위" value={c.unit} disabled={c.type === 'ok'} onChange={(e) => setCheck(k, { unit: e.target.value })} />
            <TextField select size="small" label="확인 방식" value={c.type} onChange={(e) => setCheck(k, { type: e.target.value })}>
              <MenuItem value="qty">수량 입력</MenuItem><MenuItem value="ok">정상/이상</MenuItem>
            </TextField>
            <Button color="error" size="small" onClick={() => setD({ ...d, checks: d.checks!.filter((_, i) => i !== k) })}>삭제</Button>
          </Box>
        ))}
        <Button size="small" variant="outlined" onClick={() => setD({ ...d, checks: [...(d.checks || []), { label: '새 항목', std: 1, unit: '개', type: 'qty' }] })}>+ 항목 추가</Button>
      </Box>
      {err && <Alert severity="error">{err}</Alert>}
      <Box><Button variant="contained" onClick={save}>저장</Button></Box>
    </Paper>
  );
};

// ───────── 주의 교회 ─────────
const FlagsTab: React.FC<{ flags: RentalFlag[]; reload: () => Promise<void>; toast: (m: string) => void }> = ({ flags, reload, toast }) => {
  const [regNo, setRegNo] = useState('');
  const [type, setType] = useState('반납지연');
  const [penalty, setPenalty] = useState('없음(기록만)');
  const [memo, setMemo] = useState('');
  const [err, setErr] = useState('');
  const add = async () => {
    setErr('');
    try { await rentalApi.createFlag({ regNo, type, penalty, memo }); setRegNo(''); setMemo(''); await reload(); toast('주의 교회로 기록했어요.'); } catch (e: any) { setErr(e.message); }
  };
  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography fontWeight={800} mb={1.5}>주의 교회 추가</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '140px 140px 200px minmax(0,1fr) auto' }, gap: 1.5, alignItems: 'center' }}>
          <TextField size="small" label="교회 등록번호" value={regNo} onChange={(e) => setRegNo(e.target.value.replace(/\D/g, '').slice(0, 4))} />
          <TextField select size="small" label="구분" value={type} onChange={(e) => setType(e.target.value)}>{['반납지연', '파손', '기타'].map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}</TextField>
          <TextField select size="small" label="불이익" value={penalty} onChange={(e) => setPenalty(e.target.value)}>
            {['없음(기록만)', '승인 보류', '대여 수량 제한', '일정 기간 대여 제한'].map((x) => <MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
          <TextField size="small" label="메모" value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="예: 파손 미배상" />
          <Button variant="contained" onClick={add}>추가</Button>
        </Box>
        {err && <Alert severity="error" sx={{ mt: 1.5 }}>{err}</Alert>}
      </Paper>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead><TableRow>{['교회', '구분', '내용', '불이익', '기록일', ''].map((h) => <TableCell key={h}>{h}</TableCell>)}</TableRow></TableHead>
          <TableBody>
            {!flags.length && <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>기록된 주의 교회가 없어요. 반납이 늦어지면 자동으로 기록돼요.</TableCell></TableRow>}
            {flags.map((f) => (
              <TableRow key={f._id}>
                <TableCell><b>{f.churchName || '—'}</b><Typography fontSize={11.5} color="text.secondary">{f.regNo}</Typography></TableCell>
                <TableCell><SChip s={f.type === '반납지연' ? '반납지연' : '확인필요'} extra="" /></TableCell>
                <TableCell>{f.lateDays ? `지연 ${f.lateDays}일 · ` : ''}{f.memo}{f.resNo ? ` (${f.resNo})` : ''}</TableCell>
                <TableCell>{f.penalty}</TableCell>
                <TableCell>{new Date(f.createdAt).toLocaleDateString('ko-KR')}</TableCell>
                <TableCell><Button size="small" color="error" onClick={async () => { await rentalApi.deleteFlag(f._id); await reload(); toast('해제했어요.'); }}>해제</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ───────── 설정 ─────────
const SettingsTab: React.FC<{ settings: RentalSettings; reload: () => Promise<void>; toast: (m: string) => void }> = ({ settings, reload, toast }) => {
  const [s, setS] = useState(settings);
  const [visit, setVisit] = useState((settings.visitTimes || []).join(', '));
  const [staff, setStaff] = useState((settings.staff || []).join(', '));
  const [terms, setTerms] = useState((settings.terms || []).join('\n'));
  const [hd, setHd] = useState('');
  const [hn, setHn] = useState('');
  const [err, setErr] = useState('');
  const split = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean);
  const save = async (patch: Partial<RentalSettings>, msg = '설정을 저장했어요.') => {
    setErr('');
    try { const next = await rentalApi.saveSettings(patch); setS(next); await reload(); toast(msg); } catch (e: any) { setErr(e.message); }
  };
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, display: 'grid', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', md: 'repeat(4,minmax(0,1fr))' }, gap: 1.5 }}>
        <TextField size="small" label="입금 계좌" value={s.account} onChange={(e) => setS({ ...s, account: e.target.value })} />
        <TextField size="small" label="예금주" value={s.holder} onChange={(e) => setS({ ...s, holder: e.target.value })} />
        <TextField size="small" label="입금 기한 (시간)" value={s.payDueHours} onChange={(e) => setS({ ...s, payDueHours: Number(e.target.value.replace(/\D/g, '')) || 0 })} />
        <TextField size="small" label="전액 환불 기준 (수령일 며칠 전)" value={s.cancelDays} onChange={(e) => setS({ ...s, cancelDays: Number(e.target.value.replace(/\D/g, '')) || 0 })} />
        <TextField size="small" label="최소 대여 기간 (영업일)" value={s.bizDays} onChange={(e) => setS({ ...s, bizDays: Number(e.target.value.replace(/\D/g, '')) || 0 })} />
        <TextField size="small" label="방문 시간대 (쉼표로 구분)" value={visit} onChange={(e) => setVisit(e.target.value)} />
        <TextField size="small" label="수령·반납 담당자 (쉼표로 구분)" value={staff} onChange={(e) => setStaff(e.target.value)} />
      </Box>
      <TextField multiline minRows={6} size="small" label="대여 약관 (한 줄에 한 조항)" value={terms} onChange={(e) => setTerms(e.target.value)} />
      <Box><Button variant="contained" onClick={() => save({
        account: s.account, holder: s.holder, payDueHours: s.payDueHours, cancelDays: s.cancelDays, bizDays: s.bizDays,
        visitTimes: split(visit), staff: split(staff), terms: terms.split('\n').map((x) => x.trim()).filter(Boolean),
      })}>설정 저장</Button></Box>
      <Box>
        <Typography fontWeight={800}>공휴일·본부 휴무일</Typography>
        <Typography fontSize={12.5} color="text.secondary" mb={1.5}>토요일·주일은 자동으로 막혀요. 공휴일과 본부 휴무일은 여기 등록한 날만 막혀요. 해마다 대체공휴일을 확인해 주세요.</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <TextField size="small" type="date" value={hd} onChange={(e) => setHd(e.target.value)} />
          <TextField size="small" placeholder="이름 (예: 성탄절, 본부 휴무)" value={hn} onChange={(e) => setHn(e.target.value)} />
          <Button variant="outlined" disabled={!hd} onClick={() => { save({ holidays: [...s.holidays.filter((x) => x.date !== hd), { date: hd, name: hn || '휴무' }] }, '휴무일을 추가했어요.'); setHd(''); setHn(''); }}>추가</Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {s.holidays.map((h) => <Chip key={h.date} label={`${h.date} ${h.name}`} onDelete={() => save({ holidays: s.holidays.filter((x) => x.date !== h.date) }, '휴무일을 지웠어요.')} />)}
        </Box>
      </Box>
      {err && <Alert severity="error">{err}</Alert>}
      <Typography fontSize={12} color="text.secondary">공휴일은 공공데이터포털 「특일 정보」(한국천문연구원) API로 자동으로 불러오게 바꿀 수 있어요(개발 문서 참고).</Typography>
    </Paper>
  );
};

export default RentalAdminPage;
